import { getFunctionsInstance, getFunctionsInstanceAsync } from './config';
import type { HttpsCallableOptions } from 'firebase/functions';
import { performanceMonitor } from '@/router/core/performance-monitor';

// Verbose logging helpers
const VERBOSE_LOGS = import.meta.env.VITE_FIREBASE_VERBOSE === 'true' || import.meta.env.DEV;
function verboseLog(message: string, data?: any) {
    if (VERBOSE_LOGS) {
        console.log(`[Firebase Functions] ${message}`, data || '');
    }
}
function verboseError(message: string, error?: any) {
    if (VERBOSE_LOGS) {
        console.error(`[Firebase Functions] ${message}`, error || '');
    }
}

// Export Functions using lazy initialization with async retry support
// This prevents chunking issues in production builds
let _functionsInstance: ReturnType<typeof getFunctionsInstance> | null = null;
let _functionsInitPromise: Promise<ReturnType<typeof getFunctionsInstance>> | null = null;

// Helper to get functions instance with async retry if needed
async function ensureFunctionsInstance(): Promise<ReturnType<typeof getFunctionsInstance>> {
    if (_functionsInstance) {
        return _functionsInstance;
    }
    
    if (_functionsInitPromise) {
        return await _functionsInitPromise;
    }
    
    // Try synchronous first
    try {
        _functionsInstance = getFunctionsInstance();
        return _functionsInstance;
    } catch (error: any) {
        const isComponentError = error?.message?.includes('not available') || 
                                error?.message?.includes('not been registered') ||
                                error?.message?.includes('Component') ||
                                error?.isComponentError === true;
        
        if (isComponentError) {
            // Use async version for retry
            _functionsInitPromise = getFunctionsInstanceAsync();
            _functionsInstance = await _functionsInitPromise;
            return _functionsInstance;
        }
        
        throw error;
    }
}

export const Functions = new Proxy({} as ReturnType<typeof getFunctionsInstance>, {
    get(_target, prop) {
        // If we have the instance, return immediately
        if (_functionsInstance) {
            return (_functionsInstance as any)[prop];
        }
        
        // Try to get synchronously first
        try {
            _functionsInstance = getFunctionsInstance();
            return (_functionsInstance as any)[prop];
        } catch (error: any) {
            const isComponentError = error?.message?.includes('not available') || 
                                    error?.message?.includes('not been registered') ||
                                    error?.message?.includes('Component') ||
                                    error?.isComponentError === true;
            
            if (isComponentError) {
                // For component errors, start async initialization in background
                if (!_functionsInitPromise) {
                    verboseLog('[Firebase Functions] Starting async initialization due to component error');
                    _functionsInitPromise = getFunctionsInstanceAsync().then(instance => {
                        _functionsInstance = instance;
                        verboseLog('[Firebase Functions] Async initialization completed successfully');
                        return instance;
                    }).catch(err => {
                        console.error('[Firebase Functions] Async initialization failed:', err);
                        _functionsInitPromise = null; // Allow retry
                        throw err;
                    });
                }
                
                // For methods, we need to wait - but Proxy can't be async
                // Return a function that handles async initialization
                if (typeof prop === 'string' && typeof (_functionsInstance as any)?.[prop] === 'function') {
                    return (...args: any[]) => {
                        // Wait for async init, then call the real method
                        _functionsInitPromise!.then(instance => {
                            return (instance as any)[prop](...args);
                        }).catch(err => {
                            console.error(`[Firebase Functions] Failed to call ${String(prop)}:`, err);
                            throw err;
                        });
                    };
                }
                
                // For other properties/methods, throw with a helpful message
                verboseError(`[Firebase Functions] Property '${String(prop)}' accessed before Functions is ready`, error);
                throw new Error(`Firebase Functions is not ready yet. Property '${String(prop)}' cannot be accessed until initialization completes. This usually resolves automatically within a few hundred milliseconds.`);
            }
            
            throw error;
        }
    },
    has(_target, prop) {
        if (_functionsInstance) {
            return prop in (_functionsInstance as any);
        }
        try {
            _functionsInstance = getFunctionsInstance();
            return prop in (_functionsInstance as any);
        } catch {
            return false;
        }
    },
    ownKeys(_target) {
        if (_functionsInstance) {
            return Object.keys(_functionsInstance as any);
        }
        try {
            _functionsInstance = getFunctionsInstance();
            return Object.keys(_functionsInstance as any);
        } catch {
            return [];
        }
    },
    getOwnPropertyDescriptor(_target, prop) {
        if (_functionsInstance) {
            return Object.getOwnPropertyDescriptor(_functionsInstance as any, prop);
        }
        try {
            _functionsInstance = getFunctionsInstance();
            return Object.getOwnPropertyDescriptor(_functionsInstance as any, prop);
        } catch {
            return undefined;
        }
    },
    getPrototypeOf() {
        if (_functionsInstance) {
            return Object.getPrototypeOf(_functionsInstance as any);
        }
        try {
            _functionsInstance = getFunctionsInstance();
            return Object.getPrototypeOf(_functionsInstance as any);
        } catch {
            return Object.getPrototypeOf(Object.prototype);
        }
    }
});

// Helper function to create callable functions - uses async retry
export const createCallableFunction = async <TRequest = any, TResponse = any>(name: string, options?: HttpsCallableOptions) => {
    const [functionsInstance, { httpsCallable }] = await Promise.all([
        ensureFunctionsInstance(),
        import('firebase/functions')
    ]);
    return httpsCallable<TRequest, TResponse>(functionsInstance, name, options);
};

// Base URL for Firebase onRequest functions
const FUNCTIONS_BASE_URL = `https://us-central1-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net`;

// Interface for HTTP request options
export interface HttpsRequestOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    headers?: Record<string, string>;
}

/**
 * Helper function to create an onRequest function
 * @param name - The name of the Firebase function
 * @param options - Optional HTTP request options
 * @returns A function that can be called with a payload to invoke the onRequest function
 */
export const createRequestFunction = (name: string, options?: HttpsRequestOptions) => {
    return async (payload?: any) => {
        const method = options?.method || 'POST';
        const headers = {
            'Content-Type': 'application/json',
            ...options?.headers
        };

        try {
            const safePreview = (() => {
                const MAX = 300;
                try { return JSON.stringify(payload).slice(0, MAX); } catch { return '[unserializable]'; }
            })();
            const timingId = performanceMonitor.startTiming(`fetch:${name}`, {
                type: 'fetch_function',
                function: name,
                method,
                payloadPreview: safePreview
            });
            const response = await fetch(`${FUNCTIONS_BASE_URL}/${name}`, {
                method,
                headers,
                body: method !== 'GET' ? JSON.stringify(payload || {}) : undefined,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
                performanceMonitor.endTiming(timingId, false, errorData.error || `HTTP error ${response.status}`);
                throw new Error(errorData.error || `HTTP error ${response.status}`);
            }

            const json = await response.json();
            performanceMonitor.endTiming(timingId, true);
            return json;
        } catch (error) {
            performanceMonitor.endTiming(`fetch:${name}`, false, (error as any)?.message || String(error));
            console.error(`Error calling onRequest function ${name}:`, error);
            throw error;
        }
    };
};

